import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import { NavLink } from 'react-router-dom';
import { Logo } from '../../core/components';
import styles from '../../../styles';
import { Hamburger } from './';

const MenuTransition = ({ children, ...props }) => (
  <CSSTransition {...props} timeout={1000} classNames="transition">
    {children}
  </CSSTransition>
);

const BackDrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  bottom: 0;
  box-sizing: border-box;
  padding: 3.1875rem 3.75rem;

  ${styles.media.tablet`
    padding: 3.9375rem 3.75rem;
  `} ${styles.media.phone`
    padding: 3.1875rem 1.875rem;
  `} z-index: 1000;
  transition: opacity 0.25s ${styles.easing.inOutCubic};

  &.transition-enter.transition-enter-active,
  &.transition-exit {
    opacity: 1;
  }

  &.transition-enter,
  &.transition-exit.transition-exit-active {
    opacity: 0;
  }
`;

const NavWrap = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;

  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  ul {
    .transition-enter &,
    .transition-enter.transition-enter-active & {
      li {
        &.nav-1 {
          a,
          span {
            transition-delay: 0.25s;
          }
        }
        &.nav-2 {
          a,
          span {
            //transition-delay: 0.3s;
            transition-delay: 0.275s;
          }
        }
        &.nav-3 {
          a,
          span {
            //transition-delay: 0.35s;
            transition-delay: 0.3s;
          }
        }
        &.nav-4 {
          a,
          span {
            //transition-delay: 0.4s;
            transition-delay: 0.325s;
          }
        }
        &.nav-5 {
          a,
          span {
            //transition-delay: 0.45s;
            transition-delay: 0.35s;
          }
        }
        &.nav-6 {
          a,
          span {
            //transition-delay: 0.5s;
            transition-delay: 0.375s;
          }
        }
      }
    }
    li {
      .transition-enter.transition-enter-active &,
      .transition-exit & {
        a {
          opacity: 1;
          transform: translate3d(0, 0em, 0);
          span {
            opacity: 1;
            transform: translate3d(0, 0em, 0);
          }
        }
      }
      .transition-enter &,
      .transition-exit.transition-exit-active & {
        a {
          opacity: 0;
          transform: translate3d(0, 1em, 0);
          span {
            opacity: 0;
            transform: translate3d(0, 2em, 0);
          }
        }
      }
      .transition-enter & {
        a {
          //transition-duration: 0.35s;
          transition-duration: 0.275s;
          span {
            //transition-duration: 0.5s;
            transition-duration: 0.425s;
          }
        }
      }
      .transition-exit.transition-exit-active & {
        a {
          transition-duration: 0.25s;
          span {
            transition-duration: 0.25s;
          }
        }
      }
    }
  }

  ul {
    display: flex;
    flex-direction: column;
    margin: 0 auto;

    li {
      font-family: 'ApercuBlack';
      font-size: 3.25rem;
      ${styles.media.phone`
        font-size: 2.5rem;
      `} color: ${styles.color.WHITE};
      //line-height: 1em;
      line-height: 1.75em;
      vertical-align: top;
      //margin-bottom: 0.75em;
      overflow: hidden;

      a {
        display: inline-block;
        white-space: nowrap;
        transition-property: opacity, transform;
        transition-timing-function: ${styles.easing.inOutQuad};
        color: inherit;

        div {
          &.container {
            position: relative;
            display: inline-block;
            //perspective: 20vw;
            perspective: 200vw;
            perspective-origin: 50% 50%;

            div {
              display: inline-block;
              backface-visibility: hidden;
              transition-delay: 0.05s;
              transition-duration: 0.2s, 0.25s;
              transition-timing-function: ${styles.easing.inOutQuad};
              transition-property: opacity, transform;
              transform-origin: 50% 50% -0.35em;

              &.normal {
                transform: rotateX(0deg);
                opacity: 1;
              }

              &.hover {
                position: absolute;
                left: 0;
                top: 0;
                transform: rotateX(-90deg);
                color: ${styles.color.ORANGE};
                opacity: 0;
              }
            }
          }
        }

        .current-input-mouse &:hover,
        .current-input-touch &:active {
          div.container > div {
            transition-delay: 0s;
            &.normal {
              transform: rotateX(90deg);
              opacity: 0;
            }
            &.hover {
              transform: rotateX(0deg);
              opacity: 1;
            }
          }
        }

        &.active {
          span {
            color: ${styles.color.ORANGE};
          }
        }

        span {
          font-family: 'ApercuMono';
          font-size: 1rem;
          vertical-align: top;
          line-height: 1em;
          margin: 1.5em 1em 0 0;
          display: inline-block;
          color: ${styles.color.SLATE};
          transition-property: opacity, transform;
          transition-timing-function: ${styles.easing.inOutBack};
        }
      }
    }
  }
`;

const StyledNavBar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 4.5rem;
  background-color: ${styles.color.ORANGE};
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  padding: 0 3.75rem 0 1.875rem;
  ${props =>
    props.breakpoint === 'phone' &&
    `
    padding-right: 0.9375rem;
  `} ul {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 50%;

    li {
      font-family: 'ApercuMedium';
      font-size: 1.25rem;
      color: ${styles.color.WHITE};
      text-transform: uppercase;
      margin: 0 1rem;

      a {
        display: inline-block;
        white-space: nowrap;
        color: inherit;

        &.active {
          font-family: 'ApercuBlack';
        }

        position: relative;
        display: inline;
        width: calc(100%);
        background-image: linear-gradient(
          transparent calc(100% - 2px),
          ${styles.color.WHITE} 2px
        );
        background-repeat: no-repeat;
        background-size: 0% 100%;
        transition: background-size 0.25s ${styles.easing.inOutCubic};

        &:not(.active) {
          .current-input-mouse &:hover,
          .current-input-touch &:active {
            background-size: 100% 100%;
          }
        }
      }
    }
  }
`;

const NavigationItems = [
  {
    key: '01',
    className: 'nav-1',
    to: '/',
    label: 'Home',
    exact: true,
  },
  {
    key: '02',
    className: 'nav-2',
    to: '/about',
    label: 'About the Studio',
    exact: false,
  },
  {
    key: '03',
    className: 'nav-3',
    to: '/careers',
    label: 'Careers',
    exact: false,
  },
  {
    key: '04',
    className: 'nav-4',
    to: '/team',
    label: 'Team',
    exact: false,
  },
  {
    key: '05',
    className: 'nav-5',
    to: '/games',
    label: 'Our Games',
    exact: false,
  },
  {
    key: '06',
    className: 'nav-6',
    to: '/news-events',
    label: 'News & Events',
    exact: false,
  },
];

const LegacyNavigation = props => {
  const { isOpen, onNavigate, renderLogo } = props;
  return (
    <MenuTransition in={isOpen} mountOnEnter unmountOnExit>
      <BackDrop isOpen={isOpen}>
        {renderLogo && <Logo />}
        <NavWrap isOpen={isOpen}>
          <ul>
            {NavigationItems.map(item => {
              const { key, className, to, label, exact } = item;
              return (
                <li className={className} key={key}>
                  <NavLink exact={exact} to={to} onClick={onNavigate}>
                    <span>{key}</span>
                    <div className="container">
                      <div className="normal">{label}</div>
                      <div className="hover">{label}</div>
                    </div>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </NavWrap>
      </BackDrop>
    </MenuTransition>
  );
};

LegacyNavigation.propTypes = {
  renderLogo: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

LegacyNavigation.defaultProps = {
  renderLogo: true,
};

const NavigationBar = props => {
  const { breakpoint, isOpen, onHamburgerClick } = props;
  return (
    <StyledNavBar breakpoint={breakpoint}>
      <NavLink to="/">
        <Logo />
      </NavLink>
      {breakpoint !== 'phone' ? (
        <ul>
          {NavigationItems.map(item => {
            const { key, to, label, exact } = item;
            if (to === '/') return null;
            return (
              <li key={key}>
                <NavLink exact={exact} to={to} activeClassName={'active'}>
                  {label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      ) : (
        <Hamburger isOpen={isOpen} onClick={onHamburgerClick} inline />
      )}
    </StyledNavBar>
  );
};

NavigationBar.propTypes = {
  breakpoint: PropTypes.string.isRequired,
  onHamburgerClick: PropTypes.func,
  isOpen: PropTypes.bool,
};

const Navigation = props => {
  const {
    isOpen,
    onNavigate,
    barEnabled,
    breakpoint,
    onHamburgerClick,
  } = props;
  const legacyEnabled = !barEnabled || breakpoint === 'phone';
  return (
    <React.Fragment>
      {barEnabled && (
        <NavigationBar
          breakpoint={breakpoint}
          isOpen={isOpen}
          onHamburgerClick={onHamburgerClick}
        />
      )}
      {legacyEnabled && (
        <LegacyNavigation
          isOpen={isOpen}
          onNavigate={onNavigate}
          renderLogo={breakpoint !== 'phone'}
        />
      )}
    </React.Fragment>
  );
};

Navigation.propTypes = {
  barEnabled: PropTypes.bool.isRequired,
  breakpoint: PropTypes.string.isRequired,
  onHamburgerClick: PropTypes.func,
  isOpen: PropTypes.bool,
  onNavigate: PropTypes.func,
};

export default Navigation;



// WEBPACK FOOTER //
// ./src/modules/navigation/components/Navigation.js